import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Box } from '@mui/material';
import { heritageTheme } from '../../heritageTheme.ts';
import { initializeAppData, fetchDashboards } from './actions/appActions';
import { masqueradeUtils } from '../../utils/masqueradeUtils';
import AuthManager from '../../business/AuthManager/AuthManager';
import MasqueradeBanner from '../Admin/MasqueradeBanner';
import Sidebar from '../containers/Sidebar/Sidebar';
import Dashboard from '../containers/Dashboard/Dashboard';
import DashboardEditor from '../../business/DashboardEditor/DashboardEditor';
import Screen from '../containers/Screen/Screen';
import PublicScreen from '../containers/PublicScreen/PublicScreen';
import MyWidgets from '../containers/MyWidgets/MyWidgets';
import InactivityMonitor from '../../business/InactivityMonitor/InactivityMonitor';
import AdminPanel from '../Admin/AdminPanel';
import LandingPage from '../LandingPage/LandingPage';
import UpgradeDrawer from '../../dummies/UpgradeDrawer/UpgradeDrawer';
import HeroPreviewPage from '../LandingPage/components/HeroPreviewPage';
import TermsPage from '../LandingPage/pages/TermsPage';
import PrivacyPage from '../LandingPage/pages/PrivacyPage';
import ContactPage from '../LandingPage/pages/ContactPage';

const AppContent = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  // טעינת נתונים גלובליים של האפליקציה
  useEffect(() => {
    dispatch(initializeAppData());
  }, [dispatch]);

  const {
    user,
    loading: authLoading,
    error: authError
  } = useSelector(state => state.auth);

  const {
    loading: dashboardLoading,
    error: dashboardError,
    dashboard,
    hasUnsavedChanges,
    isSaving,
    history,
    historyIndex
  } = useSelector(state => state.dashboard);

  const { catalog, predefinedTemplates } = useSelector(state => state.app);
  const organization = useSelector(state => state.organization);

  // Masquerade detection — persisted in sessionStorage per tab
  const isMasquerade = masqueradeUtils.isActive();
  const masqueradeOrgId = masqueradeUtils.getOrgId();

  // טעינת כל הדשבורדים של הארגון (לצורך "הרכיבים שלי")
  useEffect(() => {
    const effectiveOrgId = (isMasquerade && masqueradeOrgId) ? masqueradeOrgId : user?.organizationId;
    if (effectiveOrgId) {
      console.log('🔄 App.js - Loading dashboards for organization:', effectiveOrgId);
      dispatch(fetchDashboards(effectiveOrgId));
    }
  }, [user, isMasquerade, masqueradeOrgId, dispatch]);

  // הכנת myWidgets - כל הווידג'טים מכל הדשבורדים
  const allDashboards = useSelector(state => state.screens?.dashboards || []);
  const myWidgets = allDashboards.flatMap(dash =>
    (dash.current?.widgets || []).map(widget => ({
      ...widget,
      dashboardId: dash.dashboardId,
      dashboardName: dash.name
    }))
  );

  // רכיבים שמורים מהארגון
  const savedWidgets = organization?.savedWidgets || [];

  // בדיקה אם אנחנו בתצוגה המקדימה של ההירו (iframe standalone)
  const isPreviewPage = location.pathname === '/_preview';

  // בדיקה אם אנחנו בדף הנחיתה
  const isLandingPage = location.pathname === '/';

  // בדיקה אם אנחנו במצב view (תצוגה מקדימה)
  const isViewMode = location.pathname.includes('/view/');

  // בדיקה אם אנחנו במסך ציבורי (/:orgHash/:screenName)
  // מסך ציבורי = בדיוק 2 חלקים בנתיב, בלי /screens או /dashboard
  const pathParts = location.pathname.split('/').filter(Boolean);
  const isPublicScreen = pathParts.length === 2 && !pathParts.includes('screens') && !pathParts.includes('dashboard');

  // InactivityMonitor יופיע רק אם:
  // 1. יש משתמש מחובר עם organizationId
  // 2. לא במסך ציבורי
  // 3. לא בתצוגה מקדימה (view mode)
  const shouldShowInactivityMonitor = user && user.organizationId && !isPublicScreen && !isViewMode;

  // /_preview renders standalone — outside the RTL shell so Dashboard gets full 100vh
  if (isPreviewPage) return <HeroPreviewPage />;

  return (
    <>
      {/* InactivityMonitor - רק בעמודים שדורשים authorization */}
      {shouldShowInactivityMonitor && (
        <InactivityMonitor dispatch={dispatch} />
      )}

      {/* UpgradeDrawer - גלובלי, מוצג מכל מקום באפליקציה */}
      <UpgradeDrawer />

      {/* Masquerade Banner */}
      {isMasquerade && <MasqueradeBanner orgName={organization?.name} />}

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: ((user && user.organizationId) || isMasquerade) && !isViewMode ? '220px 1fr' : '1fr',
        height: isLandingPage ? 'auto' : (isMasquerade ? 'calc(100vh - 44px)' : '100vh'),
        width: '100vw',
        direction: 'rtl',
        overflow: isLandingPage ? 'visible' : 'hidden'
      }}>
        {/* Sidebar - רק למשתמשים מחוברים עם ארגון ולא במצב view */}
        {((user && user.organizationId) || isMasquerade) && !isViewMode && <Sidebar />}

        {/* Content Area */}
        <Box sx={{
          height: isLandingPage ? 'auto' : '100vh',
          overflow: isLandingPage ? 'visible' : 'hidden',
          bgcolor: isLandingPage ? 'transparent' : '#fcfbfa',
          backgroundImage: (!isLandingPage && !isPublicScreen && !isViewMode)
            ? 'radial-gradient(rgba(30,58,95,0.032) 1px, transparent 1px)'
            : 'none',
          backgroundSize: '26px 26px',
        }}>
          <Routes>

            {/* Public Screen Route - ללא לוגין */}
            <Route
              path="/:orgHash/:screenName"
              element={<PublicScreen />}
            />

            {/* רשימת מסכים - Home */}
            <Route
              path="/:organizationId/screens"
              element={
                <AuthManager
                  user={user}
                  authLoading={authLoading}
                  authError={authError}
                  dashboardLoading={false}
                  dashboardError={null}
                  organization={organization}
                  catalog={catalog}
                  predefinedTemplates={predefinedTemplates}
                  screen={null}
                  dispatch={dispatch}
                  hasUnsavedChanges={false}
                  isSaving={false}
                >
                  <Screen />
                </AuthManager>
              }
            />

            {/* הרכיבים שלי - My Widgets */}
            <Route
              path="/:organizationId/my-widgets"
              element={
                <AuthManager
                  user={user}
                  authLoading={authLoading}
                  authError={authError}
                  dashboardLoading={false}
                  dashboardError={null}
                  organization={organization}
                  catalog={catalog}
                  predefinedTemplates={predefinedTemplates}
                  screen={null}
                  dispatch={dispatch}
                  hasUnsavedChanges={false}
                  isSaving={false}
                >
                  <MyWidgets />
                </AuthManager>
              }
            />

            {/* פרטי מסך - Playlists View */}
            <Route
              path="/:organizationId/screens/:screenId"
              element={
                <AuthManager
                  user={user}
                  authLoading={authLoading}
                  authError={authError}
                  dashboardLoading={false}
                  dashboardError={null}
                  organization={organization}
                  catalog={catalog}
                  predefinedTemplates={predefinedTemplates}
                  screen={null}
                  dispatch={dispatch}
                  hasUnsavedChanges={false}
                  isSaving={false}
                >
                  <Screen />
                </AuthManager>
              }
            />

            {/* עורך Dashboard - Editor Mode */}
            <Route
              path="/:organizationId/dashboard/:dashboardId"
              element={
                <AuthManager
                  user={user}
                  authLoading={authLoading}
                  authError={authError}
                  dashboardLoading={dashboardLoading}
                  dashboardError={dashboardError}
                  organization={organization}
                  catalog={catalog}
                  predefinedTemplates={predefinedTemplates}
                  screen={dashboard}
                  dispatch={dispatch}
                  hasUnsavedChanges={hasUnsavedChanges}
                  isSaving={isSaving}
                >
                  <DashboardEditor
                    user={user}
                    organization={organization}
                    dashboard={dashboard}
                    hasUnsavedChanges={hasUnsavedChanges}
                    isSaving={isSaving}
                    dispatch={dispatch}
                    catalog={catalog}
                    history={history || []}
                    historyIndex={historyIndex || -1}
                    myWidgets={myWidgets}
                    savedWidgets={savedWidgets}
                  />
                </AuthManager>
              }
            />

            {/* צפייה בדשבורד - View Mode (Public/Read-Only) */}
            <Route
              path="/:organizationId/view/:dashboardId"
              element={
                <AuthManager
                  user={user}
                  authLoading={authLoading}
                  authError={authError}
                  dashboardLoading={dashboardLoading}
                  dashboardError={dashboardError}
                  organization={organization}
                  catalog={catalog}
                  predefinedTemplates={predefinedTemplates}
                  dashboard={dashboard}
                  dispatch={dispatch}
                  hasUnsavedChanges={false}
                  isSaving={false}
                >
                  <div style={{ position: 'relative', direction: 'ltr', width: '100%', height: '100vh' }}>
                    <Dashboard
                      user={user}
                      organization={organization}
                      onLogout={() => { }}
                      dispatch={dispatch}
                      hasUnsavedChanges={false}
                      isSaving={false}
                      isEditMode={false}
                    />
                  </div>
                </AuthManager>
              }
            />

            {/* Legacy Routes - תאימות לאחור */}
            {/* <Route
                path="/:organizationId/dashboard"
                element={
                  <AuthManager
                    user={user}
                    authLoading={authLoading}
                    authError={authError}
                    dashboardLoading={dashboardLoading}
                    dashboardError={dashboardError}
                    organization={organization}
                    screen={screen}
                    dispatch={dispatch}
                    hasUnsavedChanges={hasUnsavedChanges}
                    isSaving={isSaving}
                  />
                }
              /> */}

            {/* Root - Landing Page */}
            <Route
              path="/"
              element={
                user && user.organizationId ? (
                  <Navigate to={`/${user.organizationId}/screens`} replace />
                ) : (
                  <LandingPage />
                )
              }
            />

            {/* Login / Signup */}
            <Route
              path="/login"
              element={
                user && user.organizationId ? (
                  <Navigate to={`/${user.organizationId}/screens`} replace />
                ) : (
                  <AuthManager
                    user={user}
                    authLoading={authLoading}
                    authError={authError}
                    dashboardLoading={dashboardLoading}
                    dashboardError={dashboardError}
                    organization={organization}
                    catalog={catalog}
                    predefinedTemplates={predefinedTemplates}
                    screen={dashboard}
                    dispatch={dispatch}
                    hasUnsavedChanges={hasUnsavedChanges}
                    isSaving={isSaving}
                  />
                )
              }
            />

            {/* Admin Panel - Auth מטופל פנימית */}
            <Route
              path="/admin"
              element={<AdminPanel />}
            />

            {/* Legal & Contact Pages */}
            <Route path="/terms"   element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/contact" element={<ContactPage />} />

            {/* Catch All */}
            <Route
              path="*"
              element={
                user && user.organizationId ? (
                  <Navigate to={`/${user.organizationId}/screens`} replace />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
          </Routes>
        </Box>
      </Box>
    </>
  );
};

const App = () => {
  return (
    <ThemeProvider theme={heritageTheme}>
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
};

export default App;
